import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { DataViewModule } from 'primeng/dataview';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { FirebaseRenderData, RenderType } from '../../services/firebase.model';
import { SkeletonModule } from 'primeng/skeleton';
import { DatePipe } from '@angular/common';
import { FirebaseFunctionService } from '../../services/firebase-function.service';
import { httpsCallable } from 'firebase/functions';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogService } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { RenderPipelineDialogComponent } from '../render-pipeline-dialog/render-pipeline-dialog.component';
import { MessagesModule } from 'primeng/messages';
import { FirebaseFirestoreService } from '../../services/firebase-firestore.service';

interface RenderListItem extends FirebaseRenderData {
  isClone?: boolean
}

export enum PipelineResponses {
  success,
  error,
  insufficientFunds
}

@Component({
  selector: 'app-render-list',
  standalone: true,
  imports: [
    MessagesModule,
    DataViewModule,
    ButtonModule,
    TagModule,
    SkeletonModule,
    DatePipe,
    RippleModule,
    TooltipModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    ToastModule
  ],
  templateUrl: './render-list.component.html',
  styleUrl: './render-list.component.scss',
  providers: [ConfirmationService, DialogService]
})
export class RenderListComponent implements OnChanges {
  @Input() renderList: RenderListItem[] | undefined | null;
  @Input() renderPipelineId: string | undefined | null;
  functionsService = inject(FirebaseFunctionService);
  firestore = inject(FirebaseFirestoreService)

  loadedImages: {[key: string]: boolean} = {}
  newPipelineRequested = false

  RenderType = RenderType

  constructor(
    private confirmationService: ConfirmationService,
    private router: Router,
    private messageService: MessageService,
    private dialogService: DialogService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['renderPipelineId'] && this.renderPipelineId) {
      if (this.newPipelineRequested){
        this.openRenderStatus()
      }
      this.newPipelineRequested = false
    }
  }

  async openPipeline() {
    if (!this.renderPipelineId) {
      const resolved = await this.openPipelinesRequest()
      if (resolved === PipelineResponses.insufficientFunds) {
        this.router.navigate(['market'])
        setTimeout(
          () => this.messageService.add({
            severity: 'warn',
            life: 15 * 1000,
            summary: "Insufficient Credits or Subscription",
            detail: "Please purchase a subscription or render credits to proceed."
          }),
          500
        )
      }
    }
    else {
      this.confirmationService.confirm({
        message: 'A render pipeline is already open. Do you want to close the active render pipeline and open another?',
        header: 'Render Pipeline Conflict',
        icon: 'pi pi-exclamation-circle',
        acceptLabel: 'New Pipeline',
        acceptIcon: 'pi pi-sync',
        acceptButtonStyleClass: 'accept-button',
        rejectLabel: 'Cancel',
        rejectButtonStyleClass: 'reject-button',
        accept: () => {
          this.openPipelinesRequest()
        }
      })
    }
  }

  async openPipelinesRequest(): Promise<PipelineResponses> {
    if (!this.newPipelineRequested) {
      this.newPipelineRequested = true
      this.renderPipelineId = null
      try {
        const response = await httpsCallable(this.functionsService.functions, 'openRenderPipeline')()
        return PipelineResponses.success
      }
      catch (e: any) {
        if (e.code === 'functions/unavailable') {
          return PipelineResponses.insufficientFunds
        }
        this.messageService.add({severity: 'warn', summary: e.code, detail: e.message});
        return PipelineResponses.error
      }
    }
    return PipelineResponses.error
  }

  openRenderStatus() {
    this.dialogService.open(RenderPipelineDialogComponent, {
      header: 'Render Status',
      width: 'min(50%, 500px)',
    })
  }

  moveToEditor(id: string) {
    this.router.navigate(['editor', id])
  }

  onImageLoad(id: string) {
    this.loadedImages[id] = true
  }

  confirmDelete(event: Event, id: string): void{
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure that you want to delete this render? This action cannot be undone.',
      header: 'Confirm Delete',
      acceptIcon:"none",
      rejectIcon:"none",
      acceptLabel: 'Delete Render',
      rejectLabel: 'Cancel',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        httpsCallable(this.functionsService.functions, 'deleteRender')({renderId: id})
        if ( this.renderList ) {
          this.renderList = this.renderList.filter(render => render.id !== id)
        }
      },
    })
  }

  cloneRender(id: string) {
    if (!this.renderList) {
      return
    }
    if (this.renderList.some(render => render.isClone)) {
      this.messageService.add({severity: 'warn', summary: 'Clone In Progress', detail: 'Waiting for previous clone to finish'});
      return
    }
    const renderToClone = this.renderList.find(render => render.id === id)
    if (!renderToClone) {
      return
    }
    const index = this.renderList.indexOf(renderToClone)
    const newRender: RenderListItem = {
      ...renderToClone,
      id: '',
      isClone: true,
      thumbnailImage: '',
      renderName: `Cloning ${renderToClone.renderName}...`
    }
    this.renderList = [ newRender, ...this.renderList ]

    this.firestore.cloneRender(id)
    //httpsCallable(this.functionsService.functions, 'cloneRender')({renderId: id})
  }
}
